import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, InputGroup, Modal, Row, Spinner } from 'react-bootstrap';
import { BsArrowRight, BsClock, BsCollection, BsPencil, BsPlusLg, BsSearch, BsStars, BsTrash, BsUpload } from 'react-icons/bs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createSet, deleteSet, discardSetImage, listMySets, updateSet, uploadSetImage } from './api/setsApi';
import './SetFonts.css';
import './SetLibrary.css';

const EMPTY_FORM = {
  title: '', description: '', visibility: 'private', coverAssetId: null, coverUrl: '',
  cropX: 50, cropY: 50, cropZoom: 100,
};

const COVER_WIDTH = 700;
const COVER_HEIGHT = 980;
const EMPTY_COVER_URL = 'https://www.hearthcards.net/newsets/images/set-no-image.webp';
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatDate = (value) => value
  ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value.replace(' ', 'T')))
  : 'Unknown date';
const visibilityLabel = (value) => {
  const normalized = String(value || 'private').toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};
const ratingLabel = (value) => {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating < 1 || rating > 5) return null;
  return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
};

function CoverPreview({ set, editing = false }) {
  return (
    <div className={`set-library-cover ${editing ? 'set-library-cover-editor' : ''}`}>
      <img src={set.coverUrl || EMPTY_COVER_URL} alt={set.coverUrl ? (editing ? 'Cover preview' : `${set.title} cover`) : ''} />
    </div>
  );
}

function imageGeometry(frame, natural, crop) {
  if (!frame.width || !frame.height || !natural.width || !natural.height) return null;
  const coverScale = Math.max(frame.width / natural.width, frame.height / natural.height);
  const scale = coverScale * (crop.zoom / 100);
  const width = natural.width * scale;
  const height = natural.height * scale;
  const overflowX = Math.max(0, (width - frame.width) / 2);
  const overflowY = Math.max(0, (height - frame.height) / 2);
  return {
    width, height, overflowX, overflowY,
    translateX: ((50 - crop.x) / 50) * overflowX,
    translateY: ((50 - crop.y) / 50) * overflowY,
  };
}

function CoverCropper({ imageUrl, crop, onCropChange }) {
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const [frame, setFrame] = useState({ width: 0, height: 0 });
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const geometry = imageGeometry(frame, natural, crop);

  useEffect(() => {
    const element = frameRef.current;
    if (!element) return undefined;
    const update = () => setFrame({ width: element.clientWidth, height: element.clientHeight });
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const pointerDown = (event) => {
    if (!geometry) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { clientX: event.clientX, clientY: event.clientY, x: crop.x, y: crop.y, overflowX: geometry.overflowX, overflowY: geometry.overflowY };
  };
  const pointerMove = (event) => {
    const start = dragRef.current;
    if (!start) return;
    const x = start.overflowX > 0 ? clamp(start.x - ((event.clientX - start.clientX) / (2 * start.overflowX)) * 100, 0, 100) : 50;
    const y = start.overflowY > 0 ? clamp(start.y - ((event.clientY - start.clientY) / (2 * start.overflowY)) * 100, 0, 100) : 50;
    onCropChange({ x, y });
  };
  const pointerUp = (event) => {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <div ref={frameRef} className="set-library-cover set-library-cover-editor set-library-cropper" onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp} onPointerCancel={pointerUp}>
      <img
        src={imageUrl} alt="Cover crop preview" draggable="false"
        onLoad={(event) => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
        style={geometry ? { width: `${geometry.width}px`, height: `${geometry.height}px`, transform: `translate(calc(-50% + ${geometry.translateX}px), calc(-50% + ${geometry.translateY}px))` } : undefined}
      />
      <span className="set-library-crop-hint">Drag to position</span>
    </div>
  );
}

const loadCropImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('The cover image could not be prepared.'));
  image.src = url;
});

async function cropCoverToWebp(url, crop) {
  const image = await loadCropImage(url);
  const geometry = imageGeometry({ width: COVER_WIDTH, height: COVER_HEIGHT }, { width: image.naturalWidth, height: image.naturalHeight }, crop);
  if (!geometry) throw new Error('The cover image has invalid dimensions.');
  const canvas = document.createElement('canvas');
  canvas.width = COVER_WIDTH;
  canvas.height = COVER_HEIGHT;
  const context = canvas.getContext('2d');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, (COVER_WIDTH - geometry.width) / 2 + geometry.translateX, (COVER_HEIGHT - geometry.height) / 2 + geometry.translateY, geometry.width, geometry.height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', .88));
  if (!blob || blob.type !== 'image/webp') throw new Error('This browser could not create the cropped WebP cover.');
  return new File([blob], 'set-cover.webp', { type: 'image/webp' });
}

export default function SetLibrary() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const coverInputRef = useRef(null);
  const draftUrlRef = useRef('');
  const [sets, setSets] = useState([]);
  const [creation, setCreation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingSet, setEditingSet] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingCoverId, setPendingCoverId] = useState(null);
  const [coverDirty, setCoverDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);

  const openLinkedCard = (set) => {
    if (!set.userCardId) return;
    const next = new URLSearchParams(searchParams);
    next.set('selectedCard', String(set.userCardId));
    setSearchParams(next);
  };

  const handleCardKeyDown = (event, set) => {
    if (event.target !== event.currentTarget || !set.userCardId) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openLinkedCard(set);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    listMySets().then((payload) => {
      setSets(payload.sets || []);
      setCreation(payload.creation || null);
    })
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);
  useEffect(() => () => { if (draftUrlRef.current) URL.revokeObjectURL(draftUrlRef.current); }, []);

  const clearDraftUrl = () => {
    if (draftUrlRef.current) URL.revokeObjectURL(draftUrlRef.current);
    draftUrlRef.current = '';
  };

  const visibleSets = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return sets.filter((set) => {
      const matchesText = !needle || `${set.title} ${set.description} ${set.setId}`.toLowerCase().includes(needle);
      return matchesText && (visibility === 'all' || set.visibility === visibility);
    });
  }, [query, sets, visibility]);

  const openCreate = () => {
    if (creation && !creation.canCreate) {
      setError(creation.restrictionMessage || 'You cannot create another set right now.');
      return;
    }
    clearDraftUrl(); setEditingSet(null); setForm(EMPTY_FORM); setPendingCoverId(null); setCoverDirty(false); setShowForm(true); setError('');
  };

  const openEdit = (set) => {
    setEditingSet(set);
    setForm({
      title: set.title, description: set.description || '', visibility: set.visibility || 'private',
      coverAssetId: set.coverAssetId ?? null, coverUrl: set.coverUrl || '',
      cropX: 50, cropY: 50, cropZoom: 100,
    });
    clearDraftUrl(); setPendingCoverId(null); setCoverDirty(false); setShowForm(true); setError('');
  };

  const discardPendingCover = async () => {
    if (!pendingCoverId || !editingSet) return;
    await discardSetImage(editingSet.setId, pendingCoverId).catch(() => undefined);
    setPendingCoverId(null);
  };

  const closeForm = async () => {
    if (busy || coverBusy) return;
    setCoverBusy(true);
    await discardPendingCover();
    clearDraftUrl();
    setCoverBusy(false);
    setShowForm(false);
  };

  const uploadCover = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editingSet) return;
    setCoverBusy(true); setError('');
    try {
      if (pendingCoverId) await discardSetImage(editingSet.setId, pendingCoverId).catch(() => undefined);
      setPendingCoverId(null);
      clearDraftUrl();
      const url = URL.createObjectURL(file);
      draftUrlRef.current = url;
      setForm((current) => ({ ...current, coverUrl: url, cropX: 50, cropY: 50, cropZoom: 100 }));
      setCoverDirty(true);
    } catch (prepareError) {
      setError(prepareError.message);
    } finally { setCoverBusy(false); }
  };

  const removeCover = async () => {
    if (coverBusy) return;
    setCoverBusy(true);
    if (pendingCoverId && editingSet) await discardSetImage(editingSet.setId, pendingCoverId).catch(() => undefined);
    setPendingCoverId(null);
    clearDraftUrl();
    setForm((current) => ({ ...current, coverAssetId: null, coverUrl: '', cropX: 50, cropY: 50, cropZoom: 100 }));
    setCoverDirty(false);
    setCoverBusy(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true); setError('');
    try {
      if (editingSet) {
        let coverAssetId = form.coverAssetId;
        if (form.coverUrl && coverDirty) {
          setCoverBusy(true);
          const cropped = await cropCoverToWebp(form.coverUrl, { x: form.cropX, y: form.cropY, zoom: form.cropZoom });
          const asset = await uploadSetImage(editingSet.setId, cropped, 'cover');
          if (pendingCoverId) await discardSetImage(editingSet.setId, pendingCoverId).catch(() => undefined);
          coverAssetId = asset.assetId;
          setPendingCoverId(asset.assetId);
        }
        const payload = await updateSet({ title: form.title.trim(), description: form.description.trim(), visibility: form.visibility, coverAssetId, setId: editingSet.setId });
        setSets((current) => current.map((set) => set.setId === editingSet.setId ? { ...set, ...payload.set } : set));
        setPendingCoverId(null);
        setCoverDirty(false);
        clearDraftUrl();
        setShowForm(false);
      } else {
        const payload = await createSet({ title: form.title.trim(), description: form.description.trim() });
        setShowForm(false);
        navigate(`/sets/${payload.set.setId}`);
      }
    } catch (submitError) {
      setError(submitError.message);
    } finally { setBusy(false); setCoverBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true); setError('');
    try {
      await deleteSet(deleteTarget.setId);
      setSets((current) => current.filter((set) => set.setId !== deleteTarget.setId));
      setCreation((current) => current ? {
        ...current,
        usedSlots: Math.max(0, current.usedSlots - 1),
        remainingSlots: Math.min(current.slotLimit, current.remainingSlots + 1),
        canCreate: current.eligible,
        restrictionCode: current.eligible ? null : current.restrictionCode,
        restrictionMessage: current.eligible ? null : current.restrictionMessage,
      } : current);
      setDeleteTarget(null);
    } catch (deleteError) {
      setError(deleteError.message);
      setDeleteTarget(null);
    } finally { setBusy(false); }
  };

  return (
    <div className="set-library p-3 p-md-5">
      <section className="set-library-hero contest-frame">
        <div><span className="set-library-eyebrow">Set workshop</span><h1>My Sets</h1></div>
        <div className="set-library-creation-status">
          {creation && <div className="set-library-slots"><BsStars /><span><strong>{creation.remainingSlots}</strong> of {creation.slotLimit} set slots available</span>{creation.isPatreon && <Badge bg="warning" text="dark">Patreon</Badge>}</div>}
          <Button size="lg" onClick={openCreate} disabled={Boolean(creation && !creation.canCreate)} title={creation && !creation.canCreate ? creation.restrictionMessage : undefined}><BsPlusLg className="me-2" />New Set</Button>
        </div>
      </section>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {creation && !creation.eligible && <Alert variant="info" className="set-library-eligibility">{creation.restrictionMessage}</Alert>}
      {creation?.eligible && creation.remainingSlots === 0 && <Alert variant="warning" className="set-library-eligibility">{creation.restrictionMessage}</Alert>}
      <div className="set-library-toolbar">
        <InputGroup><InputGroup.Text><BsSearch /></InputGroup.Text><Form.Control value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search your sets..." aria-label="Search sets" /></InputGroup>
        <Form.Select value={visibility} onChange={(event) => setVisibility(event.target.value)} aria-label="Filter visibility">
          <option value="all">All visibility</option><option value="private">Private</option><option value="unlisted">Unlisted</option><option value="public">Public</option>
        </Form.Select>
      </div>

      {loading ? (
        <div className="set-library-loading"><Spinner animation="border" /><span>Loading your sets...</span></div>
      ) : visibleSets.length ? (
        <Row className="g-4">
          {visibleSets.map((set) => (
            <Col key={set.setId} xs={6} sm={6} lg={4} xl={3}>
              <div className="set-library-card-shell">
                <article
                  className={`set-library-card${set.userCardId ? ' set-library-card--clickable' : ''}`}
                  onClick={() => openLinkedCard(set)}
                  onKeyDown={(event) => handleCardKeyDown(event, set)}
                  role={set.userCardId ? 'button' : undefined}
                  tabIndex={set.userCardId ? 0 : undefined}
                  aria-label={set.userCardId ? `Open community card for ${set.title}` : undefined}
                >
                  <div className="set-library-card-cover"><CoverPreview set={set} /></div>
                  <Badge className="set-library-visibility" bg={set.visibility === 'public' ? 'success' : set.visibility === 'unlisted' ? 'warning' : 'secondary'}>{visibilityLabel(set.visibility)}</Badge>
                  <div className="set-library-card-content">
                    <h2 className="set-library-card-title" title={set.title}>{set.title}</h2>
                    <p className="set-library-card-description">{set.description || 'No description yet. Open the set to start building its page.'}</p>
                    <div className="set-library-meta"><span title={`${set.moduleCount} section${set.moduleCount === 1 ? '' : 's'}`}><BsCollection /><strong>{set.moduleCount}</strong></span><span title="Last updated"><BsClock />{formatDate(set.updatedAt)}</span></div>
                    <div className="set-library-card-actions">
                      <Button className="set-library-open" variant="light" onClick={(event) => { event.stopPropagation(); navigate(`/sets/${set.setId}`); }} aria-label={`Open ${set.title} editor`} title="Open editor"><BsArrowRight /></Button>
                      <Button variant="outline-light" onClick={(event) => { event.stopPropagation(); openEdit(set); }} aria-label={`Edit details for ${set.title}`} title="Edit details"><BsPencil /></Button>
                      <Button variant="outline-danger" onClick={(event) => { event.stopPropagation(); setDeleteTarget(set); }} aria-label={`Delete ${set.title}`} title="Delete set"><BsTrash /></Button>
                    </div>
                  </div>
                </article>
                {ratingLabel(set.rating) && <span className="set-library-rating" title={`Rating ${ratingLabel(set.rating)} out of 5`}>{ratingLabel(set.rating)}</span>}
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <div className="set-library-empty"><BsCollection /><h2>{sets.length ? 'No matching sets' : 'Build your first set page'}</h2><p>{sets.length ? 'Try a different search or visibility filter.' : 'Add sections, showcase cards, explain mechanics, and share the finished page.'}</p>{!sets.length && <Button onClick={openCreate} disabled={Boolean(creation && !creation.canCreate)}><BsPlusLg className="me-2" />Create a set</Button>}</div>
      )}

      <Modal show={showForm} onHide={closeForm} centered backdrop="static" size={editingSet ? 'lg' : undefined} dialogClassName="set-library-details-modal">
        <Form onSubmit={submit}>
          <Modal.Header closeButton={!busy && !coverBusy}><Modal.Title>{editingSet ? 'Set details' : 'Create a new set'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className={editingSet ? 'set-library-details-grid' : ''}>
              <div>
                <Form.Group className="mb-3"><Form.Label>Title</Form.Label><Form.Control autoFocus maxLength={120} value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="The name of your expansion or class" required /><Form.Text>{form.title.length}/120</Form.Text></Form.Group>
                <Form.Group className="mb-3"><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={3} maxLength={500} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="A short summary for your library" /><Form.Text>{form.description.length}/500</Form.Text></Form.Group>
                {editingSet && <Form.Group><Form.Label>Visibility</Form.Label><Form.Select value={form.visibility} onChange={(event) => setForm((current) => ({ ...current, visibility: event.target.value }))}><option value="private">Private</option><option value="unlisted">Unlisted</option><option value="public">Public</option></Form.Select></Form.Group>}
              </div>
              {editingSet && (
                <section className="set-library-cover-settings" aria-labelledby="set-cover-heading">
                  <div className="set-library-cover-heading"><h3 id="set-cover-heading">Library cover</h3><div className="set-library-cover-actions"><input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={uploadCover} /><Button type="button" size="sm" variant="outline-primary" onClick={() => coverInputRef.current?.click()} disabled={coverBusy}><BsUpload /> {form.coverUrl ? 'Replace' : 'Choose image'}</Button>{form.coverUrl && <Button type="button" size="sm" variant="outline-danger" onClick={removeCover} disabled={coverBusy}><BsTrash /></Button>}</div></div>
                  {form.coverUrl && coverDirty ? <CoverCropper imageUrl={form.coverUrl} crop={{ x: form.cropX, y: form.cropY, zoom: form.cropZoom }} onCropChange={(patch) => { setForm((current) => ({ ...current, cropX: patch.x ?? current.cropX, cropY: patch.y ?? current.cropY })); setCoverDirty(true); }} /> : <CoverPreview set={form} editing />}
                  {coverBusy && <div className="set-library-cover-busy"><Spinner size="sm" /> Preparing cropped WebP...</div>}
                  {form.coverUrl && coverDirty && <div className="set-library-crop-controls">
                    <Form.Group className="set-library-zoom-control"><Form.Label>Zoom <output>{form.cropZoom}%</output></Form.Label><Form.Range min={100} max={220} step={5} value={form.cropZoom} onChange={(event) => { setForm((current) => ({ ...current, cropZoom: Number(event.target.value) })); setCoverDirty(true); }} /></Form.Group>
                  </div>}
                </section>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={closeForm} disabled={busy || coverBusy}>Cancel</Button><Button type="submit" disabled={busy || coverBusy || !form.title.trim()}>{busy && <Spinner size="sm" className="me-2" />}{editingSet ? 'Save Details' : 'Create and open'}</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={() => !busy && setDeleteTarget(null)} centered>
        <Modal.Header closeButton={!busy}><Modal.Title>Delete set?</Modal.Title></Modal.Header>
        <Modal.Body><p>This permanently deletes <strong>{deleteTarget?.title}</strong>, its page sections, card references, and uploaded images. The cards used in this set are <strong>NOT</strong> deleted.</p><p className="text-danger mb-0">This cannot be undone.</p></Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>Keep set</Button><Button variant="danger" onClick={confirmDelete} disabled={busy}>{busy && <Spinner size="sm" className="me-2" />}Delete permanently</Button></Modal.Footer>
      </Modal>
    </div>
  );
}
